import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ChevronLeft,
  Clock,
  Lightbulb,
  AlertCircle,
  Loader,
} from "lucide-react";
import {
  usePostOvertimeMutation,
  useGetMonthlyOvertimeBalanceQuery,
} from "../../slices/overtimeApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { toast } from "sonner";

// Inline TimePicker component
function TimePicker({ initial, onSelect, onClose, minTime }) {
  // initial in 'HH:MM' 24h or empty
  const parseInitial = () => {
    if (!initial) return { hour12: 12, minute: 0, ampm: "AM" };
    const [hh, mm] = initial.split(":").map(Number);
    const ampm = hh >= 12 ? "PM" : "AM";
    let hour12 = hh % 12 === 0 ? 12 : hh % 12;
    return { hour12, minute: mm, ampm };
  };

  const [hour12, setHour12] = useState(parseInitial().hour12);
  const [minute, setMinute] = useState(parseInitial().minute);
  const [ampm, setAmpm] = useState(parseInitial().ampm);

  // Display helpers
  const formatNumber = (num) => String(num).padStart(2, "0");
  const getDisplayHour = () => formatNumber(hour12);
  const getDisplayMinute = () => formatNumber(minute);

  // minTime: optional 'HH:MM' string to disable options earlier than this
  const isDisabled = (h12, m) => {
    if (!minTime) return false;
    // convert candidate to minutes since midnight
    let hh = h12 % 12;
    if (h12 === 12) hh = 0;
    if (ampm === "PM") hh += 12;
    const candidate = hh * 60 + m;
    const [mh, mm] = minTime.split(":").map(Number);
    const minMins = mh * 60 + mm;
    return candidate < minMins;
  };
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleOk = () => {
    // convert to 24h
    let hh = hour12 % 12;
    if (hour12 === 12) hh = 0;
    if (ampm === "PM") hh += 12;
    onSelect(hh, minute);
  };

  return (
    <div className="w-full bg-white shadow-lg rounded-lg p-4">
      {/* Selected time display boxes */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 items-center">
          {/* Hour box */}
          <div className="w-12 h-10 flex items-center justify-center bg-gray-50 border rounded text-gray-800 font-medium">
            {getDisplayHour()}
          </div>
          <div className="w-12 h-10 flex items-center justify-center bg-gray-50 border rounded text-gray-800 font-medium">
            {getDisplayMinute()}
          </div>
          <button
            type="button"
            onClick={() => setAmpm(ampm === "AM" ? "PM" : "AM")}
            className={`w-14 h-10 flex items-center justify-center rounded border transition-colors ${
              ampm === "PM"
                ? "bg-gray-800 text-white"
                : "bg-gray-50 text-gray-800"
            } font-medium`}
          >
            {ampm}
          </button>
        </div>
      </div>

      {/* Time selection grid */}
      <div className="flex gap-2">
        {/* Hours */}
        <div className="w-24 border rounded overflow-hidden">
          <div className="h-48 overflow-y-auto">
            {hours.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setHour12(h)}
                className={`w-full text-left px-3 py-2 ${
                  hour12 === h ? "bg-gray-800 text-white" : "hover:bg-gray-50"
                }`}
              >
                {formatNumber(h)}
              </button>
            ))}
          </div>
        </div>

        {/* Minutes */}
        <div className="flex-1 border rounded overflow-hidden">
          <div className="h-48 overflow-y-auto">
            <div className="grid grid-cols-3 gap-px bg-gray-100">
              {minutes.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => !isDisabled(hour12, m) && setMinute(m)}
                  disabled={isDisabled(hour12, m)}
                  className={`py-1 ${
                    minute === m
                      ? "bg-gray-800 text-white"
                      : isDisabled(hour12, m)
                        ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50"
                  }`}
                >
                  {formatNumber(m)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleOk}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          OK
        </button>
      </div>
    </div>
  );
}

export default function ApplyOvertime() {
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  // Helper: generate time options in 30-minute increments (00:00 to 23:30)
  const generateTimeOptions = () => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        opts.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
      }
    }
    return opts;
  };

  const timeOptions = generateTimeOptions();

  // Convert "HH:MM" to minutes since midnight
  const timeToMins = (t) => {
    if (!t) return null;
    const [hh, mm] = t.split(":").map(Number);
    return hh * 60 + mm;
  };

  // Update computed hours whenever start or end time changes
  React.useEffect(() => {
    const s = timeToMins(startTime);
    const e = timeToMins(endTime);
    if (s == null || e == null) {
      setHours("");
      return;
    }

    // If end is earlier than start, force end = start
    if (e < s) {
      setEndTime(startTime);
      setHours("0");
      return;
    }

    const diffMins = e - s;
    const diffHours = diffMins / 60;
    // Show as decimal or integer depending on minutes
    const display = Number.isInteger(diffHours)
      ? String(diffHours)
      : String(diffHours.toFixed(2));
    setHours(display);
  }, [startTime, endTime]);

  // Real-time validation
  React.useEffect(() => {
    const newErrors = {};

    // Validate date
    if (date) {
      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0, 0);

      const { startDate, endDate } = getAllowedDateRange();
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (selectedDate < startDate || selectedDate > endDate) {
        const startFormatted = startDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const endFormatted = endDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        newErrors.date = `Overtime can only be submitted for the current month and the last 5 working days of the previous month (${startFormatted} - ${endFormatted}).`;
      }
    }

    // Validate start and end times
    if (startTime && endTime) {
      const s = timeToMins(startTime);
      const e = timeToMins(endTime);

      if (e < s) {
        newErrors.time =
          "End time must be after start time. Please adjust the timing.";
      } else if (e === s) {
        newErrors.time =
          "End time cannot be the same as start time. Please adjust the timing.";
      }
    }

    // Validate hours
    if (hours) {
      const hoursNum = parseFloat(hours);
      if (hoursNum <= 0) {
        newErrors.hours = "Hours must be greater than 0.";
      } else if (hoursNum > 24) {
        newErrors.hours = "Hours cannot exceed 24 in a single day.";
      }
    }

    setErrors(newErrors);
  }, [date, startTime, endTime, hours]);

  const startSelectRef = useRef(null);
  const endSelectRef = useRef(null);

  const openSelect = (ref) => {
    if (!ref || !ref.current) return;
    try {
      ref.current.focus();
      // Try to open native dropdown by dispatching a mousedown then click
      const evt = new MouseEvent("mousedown", {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      ref.current.dispatchEvent(evt);
      ref.current.click();
    } catch (e) {
      // Fallback to simple click
      try {
        ref.current.click();
      } catch (err) {}
    }
  };

  // New: custom TimePicker state & helper
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // 'start' | 'end' | null

  const openPicker = (which) => {
    setPickerOpenFor(which);
  };

  const closePicker = () => setPickerOpenFor(null);

  // Convert 24-hour string to a display with AM/PM
  const toAmPm = (t) => {
    if (!t) return "";
    let [hh, mm] = t.split(":").map(Number);
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 === 0 ? 12 : hh % 12;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(
      2,
      "0",
    )} ${ampm}`;
  };

  // Set time from an object {hour24: 0-23, minute: 0/30}
  const setTimeFromPicker = (which, hour24, minute) => {
    const hh = String(hour24).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    const value = `${hh}:${mm}`;
    if (which === "start") setStartTime(value);
    else setEndTime(value);
    closePicker();
  };

  // Helper to format a JS Date into local YYYY-MM-DDTHH:mm:ss (no timezone Z)
  const formatLocalDateTime = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Fetch monthly overtime balance data
  const {
    data: balanceData,
    isLoading: balanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useGetMonthlyOvertimeBalanceQuery();

  // Get user info from Redux and API
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = userInfo?.username || userInfo?.email;
  const { data: userByEmail } = useGetUserByEmailQuery(userEmail, {
    skip: !userEmail,
  });

  const academyId =
    userByEmail?.user?.academyId || userInfo?.academy_id || userInfo?.academyId;
  const currentYear = new Date().getFullYear();

  // Fetch calendar to check if it has been set up
  const { data: calendarData, isLoading: calendarLoading } =
    useGetCalendarByAcademyYearQuery(
      {
        academy_id: academyId,
        year: currentYear,
      },
      {
        skip: !academyId, // Skip if academy ID is not available
      },
    );

  // Helper: Check if a date is a working day based on calendar
  const isWorkingDay = (dateToCheck) => {
    if (!calendarData?.calendar) return true; // Default to true if no calendar

    const dateStr = dateToCheck.toISOString().split("T")[0]; // YYYY-MM-DD format
    const calendar = calendarData.calendar;

    // Check if date is in holidays
    if (calendar.holidays && Array.isArray(calendar.holidays)) {
      const isHoliday = calendar.holidays.some((h) => {
        const holidayDate = new Date(h.date).toISOString().split("T")[0];
        return holidayDate === dateStr;
      });
      if (isHoliday) return false;
    }

    // Check if date is a weekend (unless explicitly a working day)
    const dayOfWeek = dateToCheck.getDay(); // 0 = Sunday, 6 = Saturday
    if (calendar.workingDays && Array.isArray(calendar.workingDays)) {
      // If calendar specifies working days, check if this day is included
      return calendar.workingDays.includes(dayOfWeek);
    }

    // Default: exclude weekends (assume Mon-Fri are working days)
    return dayOfWeek !== 0 && dayOfWeek !== 6;
  };

  // Helper: Calculate allowed date range with last 5 WORKING days of previous month
  const getAllowedDateRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();

    // Find the last 5 working days of the previous month
    const prevMonth = new Date(year, month, 1); // First day of current month
    prevMonth.setDate(0); // Move to last day of previous month
    const lastDayOfPrevMonth = prevMonth.getDate();

    // Start from the last day of previous month and go backwards to find 5 working days
    let workingDaysFound = 0;
    let currentDate = new Date(year, month - 1, lastDayOfPrevMonth);
    const workingDaysOfPrevMonth = [];

    while (workingDaysFound < 5 && currentDate.getDate() > 0) {
      if (isWorkingDay(currentDate)) {
        workingDaysOfPrevMonth.unshift(new Date(currentDate));
        workingDaysFound++;
      }
      currentDate.setDate(currentDate.getDate() - 1);
    }

    // Start date is the earliest of the last 5 working days (or 1st of prev month if less than 5 working days)
    const startDate =
      workingDaysOfPrevMonth.length > 0
        ? workingDaysOfPrevMonth[0]
        : new Date(year, month - 1, 1);

    // End date is today
    const endDate = new Date(year, month, day);

    return { startDate, endDate };
  };

  // Calculate min and max allowed dates using useMemo to prevent unnecessary recalculations
  const { startDate: minAllowedDate, endDate: maxAllowedDate } = React.useMemo(
    () => getAllowedDateRange(),
    [calendarData], // Re-calculate when calendar data changes
  );

  // Format dates for the input constraint
  const formatDateForInput = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const minDateString = formatDateForInput(minAllowedDate);
  const maxDateString = formatDateForInput(maxAllowedDate);

  // Get balance values from API, fallback to 0 if loading
  const totalEntitled = balanceData?.monthlyLimit || 0;
  const used = balanceData?.approvedHours || 0;
  const remaining =
    balanceData?.remainingBalance !== undefined
      ? balanceData.remainingBalance
      : totalEntitled - used;
  const remainingPercentage =
    totalEntitled > 0 ? (used / totalEntitled) * 100 : 0;

  // Overtime POST mutation (RTK Query). This uses the project's apiSlice which
  // attaches Authorization headers from Redux state when available.
  const [postOvertime, { isLoading: posting }] = usePostOvertimeMutation();

  const handleSubmit = async () => {
    // Validation
    if (!date || !startTime || !endTime || !description) {
      toast.warning("Missing Information", {
        description: "Please fill in all required fields.",
      });
      return;
    }

    // Check for any validation errors
    if (Object.keys(errors).length > 0) {
      toast.error("Validation Error", {
        description: Object.values(errors).join(", "),
      });
      return;
    }

    // Check if user is trying to add hours but balance API is loading or has error
    if (balanceLoading) {
      toast.info("Please Wait", {
        description:
          "We are checking your overtime balance. Please wait a moment.",
      });
      return;
    }

    if (balanceError || !balanceData) {
      toast.error("Cannot Proceed", {
        description:
          "Unable to verify your overtime balance. Please refresh and try again.",
      });
      return;
    }

    // Check if remaining balance is sufficient
    const requestedHours = parseFloat(hours) || 0;
    if (remaining < requestedHours) {
      toast.error("Overtime Limit Exceeded", {
        description: `You cannot request this amount of overtime hours. Monthly Limit: ${totalEntitled} hours, Already Used: ${used} hours, Remaining Available: ${remaining} hours. You are requesting: ${requestedHours} hours. Shortage: ${(
          requestedHours - remaining
        ).toFixed(
          1,
        )} hours. Please reduce your overtime request or wait until the next month.`,
      });
      return;
    }

    // Build payload matching backend entity types:
    // - startTime, endTime, submittedAt -> LocalDateTime (YYYY-MM-DDTHH:mm:ss)
    // - overtimeDate -> LocalDate (YYYY-MM-DD)
    // - totalHours -> double
    const startDateTime = `${date}T${startTime}:00`;
    const endDateTime = `${date}T${endTime}:00`;
    const submittedAt = formatLocalDateTime(new Date());

    const payload = {
      startTime: startDateTime,
      endTime: endDateTime,
      totalHours: parseFloat(hours) || 0,
      description,
      overtimeDate: date,
      submittedAt,
      notes: [],
    };

    try {
      const result = await postOvertime(payload).unwrap();

      // Show success popup
      toast.success("Success!", {
        description: `Your overtime request has been submitted successfully. Request Details: Date: ${date}, Time: ${toAmPm(
          startTime,
        )} - ${toAmPm(endTime)}, Hours: ${hours} hrs. Your supervisor will review your request shortly.`,
        action: {
          label: "Go to Records",
          onClick: () => navigate("/technician/overtime/records"),
        },
      });

      // Clear form
      setDate("");
      setStartTime("");
      setEndTime("");
      setHours("");
      setDescription("");
    } catch (err) {
      const errorMsg =
        err?.data?.error ||
        err?.data?.message ||
        err?.error ||
        "Failed to submit request. Please try again.";

      // Check if this is a monthly limit error
      const isMonthlyLimitError =
        errorMsg?.toLowerCase().includes("monthly overtime limit") ||
        errorMsg?.toLowerCase().includes("exceed");

      // Check if this is a duplicate/overlapping time error
      const isDuplicateError =
        errorMsg?.toLowerCase().includes("already have an overtime request") ||
        errorMsg?.toLowerCase().includes("duplicate");

      if (isMonthlyLimitError) {
        // Parse the detailed error message
        const limitMatch = errorMsg.match(
          /Your monthly limit is ([\d.]+) hours/,
        );
        const approvedMatch = errorMsg.match(/Approved hours:\s*([\d.]+)/);
        const forwardedMatch = errorMsg.match(/Forwarded hours:\s*([\d.]+)/);
        const pendingMatch = errorMsg.match(/Pending hours:\s*([\d.]+)/);
        const totalMatch = errorMsg.match(/Total:\s*([\d.]+) hours/);
        const requestingMatch = errorMsg.match(
          /trying to add ([\d.]+) more hours/,
        );

        const monthlyLimit = limitMatch ? parseFloat(limitMatch[1]) : null;
        const totalUsed = totalMatch ? parseFloat(totalMatch[1]) : 0;
        const requestingHours = requestingMatch
          ? parseFloat(requestingMatch[1])
          : 0;

        if (monthlyLimit) {
          const shortage = totalUsed + requestingHours - monthlyLimit;
          const availableNow = Math.max(0, monthlyLimit - totalUsed);

          toast.error("Monthly Overtime Limit Exceeded", {
            description: `Shortage: ${shortage.toFixed(
              1,
            )} hrs over. Available Now: ${availableNow} hrs. Monthly Limit: ${monthlyLimit} hrs. Currently Used: ${totalUsed} hrs. Your Request: +${requestingHours} hrs.`,
          });
        } else {
          toast.error("Monthly Overtime Limit Exceeded", {
            description: errorMsg,
          });
        }
      } else if (isDuplicateError) {
        toast.error("Cannot Submit Request", {
          description: `Overlapping Overtime Request. You already have an overtime request scheduled with overlapping hours. ${errorMsg}. Choose a different date or non-overlapping time for your request.`,
        });
      } else {
        toast.error("Cannot Submit Request", {
          description: errorMsg,
        });
      }
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Calendar Not Set Message */}
      {!calendarLoading && !calendarData?.calendar && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <AlertCircle
              className="text-red-600 flex-shrink-0 mt-1"
              size={28}
            />
            <div>
              <h2 className="text-2xl font-bold text-red-900 mb-2">
                Calendar Not Configured
              </h2>
              <p className="text-red-700 mb-4">
                The academic calendar for your organization has not been set up
                yet. This is required to submit overtime requests.
              </p>
              <p className="text-red-600 text-sm">
                Please contact your administrator to configure the academic
                calendar before you can proceed with overtime requests.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with chevron */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate("/technician/overtime")}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Apply Overtime
          </h1>
        </div>
        <button
          onClick={() => navigate("/technician/overtime/records")}
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
        >
          Overtime Records
        </button>
      </div>

      {/* Show form only if calendar is set */}
      {calendarLoading ? (
        <div className="bg-white rounded-lg border p-12">
          <div className="flex items-center justify-center gap-3">
            <Loader className="animate-spin text-blue-500" size={24} />
            <span className="text-gray-600">
              Loading calendar configuration...
            </span>
          </div>
        </div>
      ) : calendarData?.calendar ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Section - Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Add overtime requests
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Enter your overtime work details including date, start time and
                end time.
              </p>

              {/* Insufficient Balance Warning */}
              {!balanceLoading &&
                !balanceError &&
                balanceData &&
                remaining <= 0 && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle
                        className="text-red-600 flex-shrink-0 mt-0.5"
                        size={20}
                      />
                      <div>
                        <h3 className="font-semibold text-red-900 mb-1">
                          Monthly Limit Reached
                        </h3>
                        <p className="text-red-700 text-sm">
                          You have used all {totalEntitled} hours of your
                          monthly overtime limit.
                          <br />
                          Your limit will reset next month. You cannot request
                          additional overtime at this time.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Low Balance Warning */}
              {!balanceLoading &&
                !balanceError &&
                balanceData &&
                remaining > 0 &&
                remaining < 5 && (
                  <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle
                        className="text-yellow-600 flex-shrink-0 mt-0.5"
                        size={20}
                      />
                      <div>
                        <h3 className="font-semibold text-yellow-900 mb-1">
                          Low Balance Warning
                        </h3>
                        <p className="text-yellow-700 text-sm">
                          You have only{" "}
                          <span className="font-semibold">
                            {remaining} hours
                          </span>{" "}
                          remaining this month. Please ensure your request
                          doesn't exceed this amount.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              <div className="space-y-6">
                {/* Date and Hours Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        min={minDateString}
                        max={maxDateString}
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 cursor-pointer pr-10 ${
                          errors.date
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      <div className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/4 text-gray-00">
                        {/* <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg> */}
                      </div>
                    </div>
                    {errors.date && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.date}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hours
                    </label>
                    <input
                      type="text"
                      placeholder="0"
                      value={hours}
                      readOnly
                      className={`w-full bg-gray-100 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                        errors.hours
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                    {errors.hours && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.hours}
                      </p>
                    )}
                  </div>
                </div>

                {/* Start Time and End Time Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time
                    </label>
                    <div className="relative">
                      <div
                        onClick={() => openPicker("start")}
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-white cursor-pointer flex items-center justify-between ${
                          errors.time ? "border-red-500" : "border-gray-300"
                        }`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            openPicker("start");
                        }}
                      >
                        <span>
                          {startTime ? toAmPm(startTime) : "--:-- --"}
                        </span>
                        <Clock className="text-gray-400" size={18} />
                      </div>

                      {pickerOpenFor === "start" && (
                        <div className="absolute mt-2 bg-white border border-gray-200 rounded shadow-lg z-20 p-2 w-64">
                          <TimePicker
                            initial={startTime}
                            onSelect={(h24, min) =>
                              setTimeFromPicker("start", h24, min)
                            }
                            onClose={closePicker}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <div className="relative">
                      <div
                        onClick={() => openPicker("end")}
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-white cursor-pointer flex items-center justify-between ${
                          errors.time ? "border-red-500" : "border-gray-300"
                        }`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            openPicker("end");
                        }}
                      >
                        <span>{endTime ? toAmPm(endTime) : "--:-- --"}</span>
                        <Clock className="text-gray-400" size={18} />
                      </div>

                      {pickerOpenFor === "end" && (
                        <div className="absolute mt-2 bg-white border border-gray-200 rounded shadow-lg z-20 p-2 w-64">
                          <TimePicker
                            initial={endTime}
                            minTime={startTime}
                            onSelect={(h24, min) =>
                              setTimeFromPicker("end", h24, min)
                            }
                            onClose={closePicker}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {errors.time && (
                  <p className="text-red-500 text-sm flex items-center gap-1 -mt-2">
                    <span>⚠</span> {errors.time}
                  </p>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe overtime
                  </label>
                  <textarea
                    placeholder="Brief description of the work"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={8}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={remaining <= 0 || balanceLoading || balanceError}
                  className={`w-full rounded-lg py-3 font-medium flex items-center justify-center gap-2 transition-colors ${
                    remaining <= 0 || balanceLoading || balanceError
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gray-800"
                  }`}
                >
                  <span className="text-xl">+</span>
                  {remaining <= 0 ? "Monthly Limit Reached" : "Submit Overtime"}
                </button>
              </div>
            </div>
          </div>

          {/* Right Section - Info Cards */}
          <div className="space-y-6">
            {/* Monthly Overtime Limit */}
            <div
              className={`bg-white rounded-lg border p-6 relative ${
                !calendarData?.calendar && !calendarLoading
                  ? "blur-sm pointer-events-none"
                  : ""
              }`}
            >
              {/* Calendar Not Set Overlay */}
              {!calendarData?.calendar && !calendarLoading && (
                <div className="absolute inset-0 bg-gray-900/20 rounded-lg backdrop-blur-sm flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4 mx-auto">
                      <AlertCircle className="text-red-600" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                      Calendar Not Configured
                    </h3>
                    <p className="text-gray-600 text-sm text-center mb-4">
                      The academic calendar must be set up before you can view
                      your overtime balance.
                    </p>
                    <p className="text-gray-500 text-xs text-center">
                      Please contact your administrator to configure the
                      calendar.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 mb-4">
                <Clock className="text-blue-500" size={20} />
                <h3 className="font-semibold text-gray-900">
                  Monthly Overtime Limit
                </h3>
              </div>

              {balanceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="animate-spin text-blue-500" size={24} />
                  <span className="ml-2 text-gray-600">Loading balance...</span>
                </div>
              ) : balanceError || !balanceData ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="text-red-500" size={18} />
                    <span className="text-red-700 font-medium">
                      Failed to load balance
                    </span>
                  </div>
                  <p className="text-red-600 text-sm mb-3">
                    {balanceError?.data?.message ||
                      balanceError?.message ||
                      "Unable to fetch overtime balance"}
                  </p>
                  {balanceError && (
                    <details className="mb-3 text-xs bg-red-100 p-2 rounded">
                      <summary className="cursor-pointer text-red-700 font-medium">
                        Error Details
                      </summary>
                      <pre className="mt-2 text-red-600 overflow-auto max-h-32">
                        {JSON.stringify(balanceError, null, 2)}
                      </pre>
                    </details>
                  )}
                  <button
                    onClick={() => refetchBalance()}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Entitled:</span>
                    <span className="font-medium text-gray-900">
                      {totalEntitled} hrs
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Used:</span>
                    <span className="font-medium text-gray-900">
                      -{used} hrs
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remaining</span>
                    <span className="font-medium text-blue-600">
                      {remaining} hrs
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${remainingPercentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="text-yellow-500" size={20} />
                <h3 className="font-semibold text-gray-900">How it works</h3>
              </div>

              <ol className="space-y-4 text-sm text-gray-600">
                <li className="flex gap-3">
                  <span className="font-medium text-gray-900 flex-shrink-0">
                    1.
                  </span>
                  <span>
                    Technician submits an overtime request with details and
                    category.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-medium text-gray-900 flex-shrink-0">
                    2.
                  </span>
                  <span>
                    Supervisor reviews and forwards to the manager with an
                    optional note.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-medium text-gray-900 flex-shrink-0">
                    3.
                  </span>
                  <span>
                    Manager approves or rejects, with visibility into who
                    forwarded which employee and full overtime details.
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border p-12">
          <div className="flex items-center justify-center gap-4 text-center">
            <AlertCircle className="text-red-500 flex-shrink-0" size={32} />
            <div>
              <p className="text-red-700 font-semibold text-lg">
                No calendar configured
              </p>
              <p className="text-red-600 text-sm">
                Please contact your administrator to configure the calendar.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
